import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { EmptyState } from '@/components/shared/EmptyState'
import { StatusBadge } from '@/components/shared/StatusBadge'
import {
  Download,
  Plus,
  Trash2,
  Loader2,
  Play,
  XCircle,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Eye,
  CheckCircle2,
  ShieldCheck,
  AlertCircle,
  Mail,
  Server,
  FolderOpen,
  Target,
  FileCheck,
  ExternalLink,
  Unplug,
} from 'lucide-react'

// CHECKPOINT: PRD-10 FR-10.8.1 — Migration job data model matches existing ImportJob interface.
interface ImportJob {
  id: string
  source_email: string
  source_host: string
  source_username: string
  target_domain_id: string
  target_mailbox_id: string | null
  status: string
  percentage: number
  created_at: string
  started_at: string | null
  estimated_completion: string | null
  current_folder: string | null
  messages_total: number | null
  messages_synced: number
  messages_skipped: number
  messages_failed: number
  fail_reason: string | null
}

interface Domain {
  id: string
  name: string
}

interface Mailbox {
  id: string
  email: string
}

interface PreviewData {
  folders: { name: string; delimiter: string; flags: string[]; messages: number }[]
  preview_folder: string | null
  previews: { uid: number; subject: string; from: string; date: string }[]
}

const PROVIDER_PRESETS = [
  { label: 'Generic / Other', value: 'other', host: '', port: 993, ssl: true },
  { label: 'cPanel / WHM', value: 'cpanel', host: 'mail.{domain}', port: 993, ssl: true },
  { label: 'Namecheap', value: 'namecheap', host: 'mail.{domain}', port: 993, ssl: true },
  { label: 'Ionos / 1&1', value: 'ionos', host: 'imap.ionos.com', port: 993, ssl: true },
  { label: 'Hostinger', value: 'hostinger', host: 'imap.hostinger.com', port: 993, ssl: true },
  { label: 'Zoho Mail', value: 'zoho', host: 'imap.zoho.com', port: 993, ssl: true },
  { label: 'Fastmail', value: 'fastmail', host: 'imap.fastmail.com', port: 993, ssl: true },
]

const FOLDER_OPTIONS = ['INBOX', 'Sent', 'Drafts', 'Archive', 'Trash', 'Junk']

export function MigrationCenter() {
  const [imports, setImports] = useState<ImportJob[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [addOpen, setAddOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState(1)
  const [creating, setCreating] = useState(false)
  const [testing, setTesting] = useState(false)
  const [domains, setDomains] = useState<Domain[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [domainsLoading, setDomainsLoading] = useState(false)
  const [previewJob, setPreviewJob] = useState<ImportJob | null>(null)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})
  const [form, setForm] = useState({
    provider: 'other',
    source_email: '',
    source_host: '',
    source_username: '',
    source_password: '',
    imap_port: 993,
    imap_use_ssl: true,
    validate_certs: true,
    domain_id: '',
    mailbox_id: '',
    selected_folders: ['INBOX', 'Sent'],
  })
  const [testResult, setTestResult] = useState<{ ok: boolean; folders: { name: string; messages: number }[] } | null>(null)

  // CHECKPOINT: PRD-10 FR-10.8.2 — Fetch migration jobs with pagination.
  const fetchImports = useCallback(async () => {
    try {
      const res = await api.get(`/imports?page=${page}&per_page=20`)
      setImports(res.data.imports || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load migrations')
    } finally {
      setLoading(false)
    }
  }, [page])

  const fetchDomains = async () => {
    setDomainsLoading(true)
    try {
      const res = await api.get('/domains')
      setDomains((res.data.domains || []))
    } catch {
      toast.error('Failed to load domains')
    } finally {
      setDomainsLoading(false)
    }
  }

  const fetchMailboxes = async (domainId: string) => {
    try {
      const res = await api.get(`/mailboxes?domain_id=${domainId}&per_page=100`)
      setMailboxes(res.data.mailboxes || [])
    } catch {
      toast.error('Failed to load mailboxes')
      setMailboxes([])
    }
  }

  useEffect(() => {
    fetchImports()
  }, [fetchImports])

  useEffect(() => {
    if (addOpen) {
      fetchDomains()
    }
  }, [addOpen])

  useEffect(() => {
    if (form.domain_id) {
      fetchMailboxes(form.domain_id)
    } else {
      setMailboxes([])
    }
  }, [form.domain_id])

  // CHECKPOINT: PRD-10 FR-10.8.5 — Poll active migration jobs every 3 seconds.
  useEffect(() => {
    const hasActive = imports.some((j) => ['connecting', 'syncing'].includes(j.status))
    if (!hasActive) return
    const interval = setInterval(fetchImports, 3000)
    return () => clearInterval(interval)
  }, [imports, fetchImports])

  const resetWizard = () => {
    setWizardStep(1)
    setForm({
      provider: 'other',
      source_email: '',
      source_host: '',
      source_username: '',
      source_password: '',
      imap_port: 993,
      imap_use_ssl: true,
      validate_certs: true,
      domain_id: '',
      mailbox_id: '',
      selected_folders: ['INBOX', 'Sent'],
    })
    setTestResult(null)
  }

  const handleOpenWizard = () => {
    resetWizard()
    setAddOpen(true)
  }

  const handleCloseWizard = () => {
    setAddOpen(false)
    resetWizard()
  }

  const canProceedFromCredentials = () => {
    return !!form.source_host && !!form.source_username && !!form.source_password
  }

  const toggleFolder = (folder: string) => {
    setForm((f) => {
      const current = new Set(f.selected_folders)
      if (current.has(folder)) current.delete(folder)
      else current.add(folder)
      return { ...f, selected_folders: Array.from(current) }
    })
  }

  const handleTestConnection = async () => {
    if (!form.source_host || !form.source_username || !form.source_password) {
      toast.error('Please fill in host, username, and password')
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post('/imports/test-connection', {
        source_email: form.source_email || form.source_username,
        imap_host: form.source_host,
        imap_port: form.imap_port,
        imap_username: form.source_username,
        imap_password: form.source_password,
        imap_use_ssl: form.imap_use_ssl,
        validate_certs: form.validate_certs,
      })
      setTestResult(res.data)
      toast.success('IMAP connection successful')
    } catch (err: any) {
      const msg = err.response?.data?.detail?.message || err.response?.data?.detail || 'IMAP connection failed'
      toast.error(typeof msg === 'string' ? msg : 'IMAP connection failed')
    } finally {
      setTesting(false)
    }
  }

  const buildCreatePayload = () => {
    const sync_drafts = form.selected_folders.includes('Drafts')
    const sync_trash = form.selected_folders.includes('Trash')
    const skipped = FOLDER_OPTIONS.filter((f) => !form.selected_folders.includes(f)).map((f) => {
      if (f === 'Junk') return 'Junk'
      if (f === 'Trash' && !sync_trash) return 'Trash'
      if (f === 'Drafts' && !sync_drafts) return 'Drafts'
      return f
    })
    return {
      source_email: form.source_email || form.source_username,
      target_domain_id: form.domain_id,
      target_mailbox_id: form.mailbox_id || undefined,
      imap_host: form.source_host,
      imap_port: form.imap_port,
      imap_use_ssl: form.imap_use_ssl,
      validate_certs: form.validate_certs,
      imap_username: form.source_username,
      imap_password: form.source_password,
      sync_drafts,
      sync_trash,
      skipped_folders: skipped,
    }
  }

  // CHECKPOINT: PRD-10 FR-10.8.4 — Create migration job then fetch a preview before starting sync.
  const handleCreate = async () => {
    if (!canProceedFromCredentials() || !form.domain_id) {
      toast.error('Please fill in all required fields')
      return
    }
    setCreating(true)
    try {
      const res = await api.post('/imports', buildCreatePayload())
      const jobId = res.data.import?.id || res.data.id
      if (!jobId) {
        toast.success(res.data.message || 'Migration created')
        handleCloseWizard()
        fetchImports()
        return
      }
      const job = { ...res.data.import, id: jobId } as ImportJob
      setPreviewJob(job)
      setPreviewLoading(true)
      setPreviewData(null)
      try {
        const previewRes = await api.post(`/imports/${jobId}/preview`)
        setPreviewData(previewRes.data)
        setWizardStep(5)
      } catch (err: any) {
        toast.error(err.response?.data?.detail?.message || 'Failed to preview source')
      } finally {
        setPreviewLoading(false)
      }
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to create migration')
    } finally {
      setCreating(false)
    }
  }

  const handleStart = async (id: string) => {
    setActionLoading((prev) => ({ ...prev, [id]: true }))
    try {
      const res = await api.post(`/imports/${id}/start`)
      toast.success(res.data.message || 'Migration started')
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to start migration')
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const handleComplete = async (id: string) => {
    setActionLoading((prev) => ({ ...prev, [id]: true }))
    try {
      const res = await api.post(`/imports/${id}/complete`)
      toast.success(res.data.message || 'Migration completed')
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to complete migration')
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const handleCancel = async (id: string) => {
    setActionLoading((prev) => ({ ...prev, [id]: true }))
    try {
      const res = await api.post(`/imports/${id}/cancel`)
      toast.success(res.data.message || 'Migration cancelled')
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to cancel migration')
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this migration job?')) return
    try {
      await api.delete(`/imports/${id}`)
      toast.success('Migration deleted')
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to delete migration')
    }
  }

  const handlePreview = async (job: ImportJob) => {
    setPreviewJob(job)
    setPreviewLoading(true)
    setPreviewData(null)
    try {
      const res = await api.post(`/imports/${job.id}/preview`)
      setPreviewData(res.data)
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to preview source')
    } finally {
      setPreviewLoading(false)
    }
  }

  // CHECKPOINT: PRD-10 FR-10.8.6 — Cutover flow: complete then delete source with confirmation.
  const handleDisconnectSource = async (id: string) => {
    if (!confirm('Disconnect the source mailbox? This will finalize the migration and remove the job.')) return
    setActionLoading((prev) => ({ ...prev, [id]: true }))
    try {
      await api.post(`/imports/${id}/complete`)
      await api.delete(`/imports/${id}`)
      toast.success('Source disconnected and migration finalized')
      fetchImports()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to disconnect source')
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const selectedDomain = domains.find((d) => d.id === form.domain_id)
  const selectedMailbox = mailboxes.find((m) => m.id === form.mailbox_id)

  const renderWizardStep = () => {
    switch (wizardStep) {
      // CHECKPOINT: PRD-10 FR-10.8.4.2 — Step 1: Generic IMAP credentials and connection test.
      case 1:
        return (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm">
              <p className="font-medium text-blue-400">One migration = one mailbox</p>
              <p className="text-muted-foreground">
                To move support@, hello@, and founder@, create a separate migration for each address.
                We do not support Gmail, Yahoo, or Outlook in this release.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Quick preset</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => {
                  const preset = PROVIDER_PRESETS.find((p) => p.value === v)
                  if (!preset) return
                  setForm((f) => ({
                    ...f,
                    provider: v,
                    source_host: preset.host,
                    imap_port: preset.port,
                    imap_use_ssl: preset.ssl,
                  }))
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Source email</Label>
              <Input
                placeholder="you@example.com"
                value={form.source_email}
                onChange={(e) => setForm((f) => ({ ...f, source_email: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">The mailbox you are moving away from.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2 col-span-2">
                <Label>IMAP Host</Label>
                <Input
                  placeholder="imap.example.com"
                  value={form.source_host}
                  onChange={(e) => setForm((f) => ({ ...f, source_host: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={form.imap_port}
                  onChange={(e) => setForm((f) => ({ ...f, imap_port: Number(e.target.value) }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Connection Security</Label>
              <Select
                value={form.imap_use_ssl ? 'ssl' : 'starttls'}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    imap_use_ssl: v === 'ssl',
                    imap_port: v === 'ssl' ? 993 : 143,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl">SSL / TLS (993)</SelectItem>
                  <SelectItem value="starttls">STARTTLS (143)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>IMAP Username</Label>
              <Input
                placeholder="you@example.com"
                value={form.source_username}
                onChange={(e) => setForm((f) => ({ ...f, source_username: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Password / App Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={form.source_password}
                onChange={(e) => setForm((f) => ({ ...f, source_password: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                We encrypt this while the migration runs and delete it when the migration finishes or is cancelled.
              </p>
            </div>

            <div className="flex items-start gap-2 rounded-lg border p-3">
              <Checkbox
                id="validate-certs"
                checked={form.validate_certs}
                onCheckedChange={(checked) => setForm((f) => ({ ...f, validate_certs: checked === true }))}
              />
              <div className="grid gap-1 leading-none">
                <label
                  htmlFor="validate-certs"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Validate SSL/TLS certificates
                </label>
                <p className="text-xs text-muted-foreground">
                  Turn off only if your provider uses a self-signed certificate.
                </p>
              </div>
            </div>

            <Button variant="outline" onClick={handleTestConnection} disabled={testing} className="w-full">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Test Connection
            </Button>
            {testResult && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Connection successful — {testResult.folders.length} folders found
                </p>
                <div className="flex flex-wrap gap-2">
                  {testResult.folders.map((f) => (
                    <Badge key={f.name} variant="outline">
                      {f.name} ({f.messages})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      // CHECKPOINT: PRD-10 FR-10.8.4.3 — Step 2: Folder selection with sensible defaults.
      case 2:
        return (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Select the folders to migrate.</p>
            <div className="space-y-2">
              {FOLDER_OPTIONS.map((folder) => (
                <div key={folder} className="flex items-center gap-3 rounded-lg border p-3">
                  <Checkbox
                    id={`folder-${folder}`}
                    checked={form.selected_folders.includes(folder)}
                    onCheckedChange={() => toggleFolder(folder)}
                  />
                  <Label htmlFor={`folder-${folder}`} className="flex-1 text-sm font-normal cursor-pointer">
                    {folder}
                  </Label>
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </div>
        )
      // CHECKPOINT: PRD-10 FR-10.8.4.4 — Step 3: Target domain and mailbox selection.
      case 3:
        return (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Target Domain</Label>
              <Select
                value={form.domain_id}
                onValueChange={(v) => setForm((f) => ({ ...f, domain_id: v, mailbox_id: '' }))}
                disabled={domainsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a domain" />
                </SelectTrigger>
                <SelectContent>
                  {domains.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Mailbox</Label>
              <Select
                value={form.mailbox_id}
                onValueChange={(v) => setForm((f) => ({ ...f, mailbox_id: v }))}
                disabled={!form.domain_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a mailbox" />
                </SelectTrigger>
                <SelectContent>
                  {mailboxes.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )
      // CHECKPOINT: PRD-10 FR-10.8.4.5 — Step 4: Review summary before creating the job.
      case 4:
        return (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Review your migration before previewing source data.</p>
            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Source:</span>
                  <span className="font-medium">{form.source_email || form.source_username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Server className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Server:</span>
                  <span className="font-medium">{form.source_host}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Folders:</span>
                  <span className="font-medium">{form.selected_folders.join(', ') || 'None'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Target:</span>
                  <span className="font-medium">
                    {selectedMailbox?.email || selectedDomain?.name || 'Not selected'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      // CHECKPOINT: PRD-10 FR-10.8.4.6 — Step 5: Preview source folders/messages before starting sync.
      case 5:
        return (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Preview what will be migrated from the source mailbox.</p>
            {previewLoading ? (
              <div className="py-12 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : previewData ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Folders</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {previewData.folders.map((f) => (
                      <Badge key={f.name} variant="outline">
                        {f.name} ({f.messages})
                      </Badge>
                    ))}
                  </div>
                </div>
                {previewData.preview_folder && (
                  <div>
                    <Label className="text-xs">Preview from {previewData.preview_folder}</Label>
                    <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                      {previewData.previews.length === 0 && (
                        <p className="text-sm text-muted-foreground">No messages found in this folder.</p>
                      )}
                      {previewData.previews.map((p) => (
                        <Card key={p.uid}>
                          <CardContent className="p-3 text-sm space-y-1">
                            <div className="font-medium truncate">{p.subject}</div>
                            <div className="text-muted-foreground text-xs">From: {p.from}</div>
                            <div className="text-muted-foreground text-xs">{p.date}</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4">No preview data available.</p>
            )}
          </div>
        )
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Migration Center</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Migrate emails from external IMAP providers into your PurpleToad Mail mailboxes.
            Each migration moves exactly one mailbox — repeat for additional addresses.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/imports/guide">
              <BookOpen className="mr-2 h-4 w-4" />
              Guide
            </Link>
          </Button>
          <Button
            onClick={handleOpenWizard}
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Migration
          </Button>
        </div>
      </div>

      {imports.length === 0 ? (
        <EmptyState
          title="No migrations yet"
          description="Move one mailbox at a time. For example, migrate support@yourdomain.com first, then repeat for hello@ and founder@."
          icon={<Download className="h-5 w-5" />}
          action={
            <Button onClick={handleOpenWizard}>
              <Plus className="mr-2 h-4 w-4" />
              New Migration
            </Button>
          }
        />
      ) : (
        <>
          {/* CHECKPOINT: PRD-10 FR-10.8.2 — Migration job list cards with status, progress, and actions. */}
          <div className="grid gap-3">
            {imports.map((job) => (
              <Card key={job.id} className="hover:bg-accent/20 transition-colors">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{job.source_username}</span>
                        <span className="text-xs text-muted-foreground">{job.source_host}</span>
                        <StatusBadge status={job.status} />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {job.source_email} · Created {new Date(job.created_at).toLocaleString()}
                      </div>
                      {['connecting', 'syncing', 'sync_complete'].includes(job.status) && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Progress value={job.percentage} className="h-1.5 w-48" />
                            <span className="text-xs text-muted-foreground">{job.percentage}%</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {job.current_folder && <span className="mr-2">Folder: {job.current_folder}</span>}
                            <span>
                              {job.messages_synced} synced
                              {job.messages_skipped ? ` · ${job.messages_skipped} skipped` : ''}
                              {job.messages_failed ? ` · ${job.messages_failed} failed` : ''}
                              {job.messages_total ? ` / ${job.messages_total} total` : ''}
                            </span>
                          </div>
                        </div>
                      )}
                      {job.status === 'failed' && job.fail_reason && (
                        <div className="flex items-start gap-2 text-xs text-red-400">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          <span>{job.fail_reason}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {job.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handlePreview(job)}
                            title="Preview source"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleStart(job.id)}
                            disabled={actionLoading[job.id]}
                            title="Start migration"
                          >
                            {actionLoading[job.id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      {['connecting', 'syncing'].includes(job.status) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                          onClick={() => handleCancel(job.id)}
                          disabled={actionLoading[job.id]}
                          title="Cancel migration"
                        >
                          {actionLoading[job.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {job.status === 'sync_complete' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-400 hover:text-green-300"
                          onClick={() => handleComplete(job.id)}
                          disabled={actionLoading[job.id]}
                          title="Complete migration"
                        >
                          {actionLoading[job.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-400 hover:text-red-300"
                        onClick={() => handleDelete(job.id)}
                        title="Delete migration"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* CHECKPOINT: PRD-10 FR-10.8.7 — Cutover guidance for sync_complete/completed jobs. */}
                  {['sync_complete', 'completed'].includes(job.status) && (
                    <div className="rounded-lg border bg-card/50 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <FileCheck className="h-4 w-4 text-purple-400" />
                        <span className="text-sm font-medium">Ready for cutover</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Point your domain's MX record to <code className="rounded bg-accent px-1 py-0.5">mail.purpletoadmail.com</code>{' '}
                        to begin receiving new mail at PurpleToad Mail.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href="https://docs.purpletoadmail.com/docs/migration/switch-mx"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="mr-2 h-3.5 w-3.5" />
                            Switch MX records
                          </a>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDisconnectSource(job.id)}
                          disabled={actionLoading[job.id]}
                        >
                          {actionLoading[job.id] ? (
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Unplug className="mr-2 h-3.5 w-3.5" />
                          )}
                          Disconnect source
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* CHECKPOINT: PRD-10 FR-10.8.4 — 4-step migration creation wizard dialog. */}
      <Dialog open={addOpen} onOpenChange={(open) => !open && handleCloseWizard()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Migration</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
            {['Credentials', 'Folders', 'Target', 'Review', 'Preview'].map((label, idx) => (
              <div key={label} className={`flex flex-col items-center gap-1 ${wizardStep === idx + 1 ? 'text-purple-400' : ''}`}>
                <div
                  className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium ${
                    wizardStep === idx + 1
                      ? 'bg-purple-500/20 text-purple-400'
                      : wizardStep > idx + 1
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-accent'
                  }`}
                >
                  {wizardStep > idx + 1 ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx + 1}
                </div>
                <span className="hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>
          <Separator />
          {renderWizardStep()}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {wizardStep > 1 && wizardStep < 5 && (
              <Button variant="outline" onClick={() => setWizardStep((s) => s - 1)} className="w-full sm:w-auto">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            {wizardStep === 5 && (
              <Button variant="outline" onClick={() => setWizardStep(4)} className="w-full sm:w-auto">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            {wizardStep < 4 ? (
              <Button
                onClick={() => setWizardStep((s) => s + 1)}
                disabled={
                  (wizardStep === 1 && !canProceedFromCredentials()) ||
                  (wizardStep === 2 && form.selected_folders.length === 0) ||
                  (wizardStep === 3 && !form.domain_id)
                }
                className="w-full sm:w-auto"
              >
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : wizardStep === 4 ? (
              <Button
                onClick={handleCreate}
                disabled={creating || !canProceedFromCredentials() || !form.domain_id}
                className="w-full sm:w-auto bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
              >
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create & Preview
              </Button>
            ) : (
              <Button
                onClick={() => previewJob && handleStart(previewJob.id)}
                disabled={creating || !previewJob}
                className="w-full sm:w-auto bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
              >
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start Migration
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewJob} onOpenChange={(open) => !open && setPreviewJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview source</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : previewData ? (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">Folders</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {previewData.folders.map((f) => (
                    <Badge key={f.name} variant="outline">
                      {f.name} ({f.messages})
                    </Badge>
                  ))}
                </div>
              </div>
              {previewData.preview_folder && (
                <div>
                  <Label className="text-xs">Preview from {previewData.preview_folder}</Label>
                  <div className="mt-2 space-y-2">
                    {previewData.previews.length === 0 && (
                      <p className="text-sm text-muted-foreground">No messages found in this folder.</p>
                    )}
                    {previewData.previews.map((p) => (
                      <Card key={p.uid}>
                        <CardContent className="p-3 text-sm space-y-1">
                          <div className="font-medium truncate">{p.subject}</div>
                          <div className="text-muted-foreground text-xs">From: {p.from}</div>
                          <div className="text-muted-foreground text-xs">{p.date}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">No preview data available.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
